import React from 'react';
import { Grid, Skeleton, Box, Stack, Card, CardContent, useTheme, Divider } from '@mui/material';

interface SkeletonCardProps {
  children?: React.ReactNode;
  height?: string | number;
  sx?: any;
  contentSx?: any;
}

const SubscriptionsPageSkeleton = () => {
  const theme = useTheme();
  const GRID_SPACING = { xs: 1, sm: 2 };

  const SkeletonCard = ({ children, height, sx = {}, contentSx = {} }: SkeletonCardProps) => (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 4,
        border: `1px solid ${theme.palette.divider}`,
        ...sx
      }}
    >
      <CardContent
        sx={{
          height: '100%',
          p: { xs: 1.5, md: 2.5 },
          '&:last-child': { pb: { xs: 1.5, md: 2.5 } },
          ...contentSx
        }}
      >
        {children ? children : <Skeleton variant="rectangular" height={height || '100%'} sx={{ borderRadius: 2 }} />}
      </CardContent>
    </Card>
  );

  const TableSkeleton = ({ rows = 5, hasHeaderAction = false }) => (
    <Stack spacing={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Skeleton width={140} height={32} variant="text" sx={{ borderRadius: 1 }} />
        {hasHeaderAction && <Skeleton width={160} height={40} variant="rectangular" sx={{ borderRadius: 1 }} />}
      </Box>
      <Stack spacing={1}>
        {[...Array(rows)].map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={50} sx={{ borderRadius: 1 }} />
        ))}
      </Stack>
    </Stack>
  );

  const ProgressSectionSkeleton = () => (
    <Box sx={{ mb: 2 }}>
      <Skeleton width={150} height={24} sx={{ mb: 1 }} />
      <Skeleton variant="rectangular" height={8} sx={{ borderRadius: 1, mb: 1 }} />
      <Box display="flex" justifyContent="space-between">
        <Skeleton width={100} height={16} />
        <Skeleton width={60} height={16} />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ p: 0 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={2}
        mb={{ xs: 2, md: 3 }}
        ml={{ xs: 0.5, md: 1.5 }}
      >
        <Stack spacing={1}>
          <Skeleton variant="text" width={150} height={40} />
        </Stack>
        <Stack spacing={1} display="flex" alignItems="center" justifyContent="center" gap={1} flexDirection={{ xs: 'column', sm: 'row' }}>
          <Skeleton variant="text" width={200} height={20} />
          <Skeleton variant="rectangular" width={200} height={48} sx={{ borderRadius: '10px' }} />
        </Stack>
      </Box>

      <Grid container spacing={GRID_SPACING} mb={{ xs: 2, md: 3 }}>
        {[...Array(4)].map((_, index) => (
          <Grid item xs={6} sm={3} key={`stat-${index}`}>
            <SkeletonCard>
              <Stack spacing={1.5} justifyContent="center" height="100%">
                <Skeleton variant="text" width="50%" height={20} />
                <Skeleton variant="text" width="60%" height={40} />
                <Skeleton variant="text" width="30%" height={20} />
              </Stack>
            </SkeletonCard>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={GRID_SPACING}>
        <Grid item xs={12} md={7}>
          <Stack spacing={GRID_SPACING}>
            <SkeletonCard>
              <TableSkeleton rows={2} hasHeaderAction={true} />
            </SkeletonCard>
            <SkeletonCard>
              <TableSkeleton rows={6} />
            </SkeletonCard>
          </Stack>
        </Grid>

        <Grid item xs={12} md={5}>
          <SkeletonCard>
            <Stack spacing={3}>
              <Box>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Skeleton variant="circular" width={24} height={24} />
                  <Skeleton width={140} height={28} />
                </Box>
                <ProgressSectionSkeleton />
                <ProgressSectionSkeleton />
              </Box>

              <Divider />

              <Box>
                <Skeleton width={180} height={24} sx={{ mb: 2 }} />
                <Stack spacing={1.5}>
                  <Box display="flex" justifyContent="space-between">
                    <Skeleton width="60%" height={20} />
                    <Skeleton width="20%" height={20} />
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Skeleton width="50%" height={24} />
                    <Skeleton width="25%" height={24} />
                  </Box>
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Skeleton width={140} height={24} sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  {[...Array(2)].map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        p: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={2}>
                        <Skeleton variant="rectangular" width={40} height={25} sx={{ borderRadius: 0.5 }} />
                        <Box>
                          <Skeleton width={120} height={20} />
                          <Skeleton width={80} height={16} />
                        </Box>
                      </Box>
                      <Skeleton width={40} height={30} sx={{ borderRadius: 1 }} />
                    </Box>
                  ))}
                  <Skeleton width={180} height={20} />
                </Stack>
              </Box>
            </Stack>
          </SkeletonCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SubscriptionsPageSkeleton;
